/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { Fragment, useState } from 'react';
import { FieldProps, FormGenerator, FormOptionInterface } from '../../form/FormGenerator';
import { Form } from '../EditMetricModal';
import { Button } from 'reactstrap';
import { InputType } from 'reactstrap/types/lib/Input';
import { Alternative, LanguageAlternative } from '../../../types/universalTracker';
import { languages } from '../constants';
import { QUESTION } from '../../../constants/terminology';

const availableLanguages = languages.map(l => l.code);

type AlternativeFieldCodes = 'languageCode' | 'valueLabel' | 'instructions';
interface LanguageField extends FieldProps {
  code: AlternativeFieldCodes
}

const getLanguageFields = (): LanguageField[] => ([
  {
    code: 'languageCode',
    type: 'select' as InputType,
    required: true,
    label: 'Which language?',
    placeholder: 'Select language',
    options: [
      ...languages.map(l => ({ value: l.code, label: l.name }))
    ],
    disabledCallback: (v) => !!v.languageCode, // Disable as soon as set. Otherwise when you change then the UI keeps re-ordering on change, which felt weird.
    classes: {
      label: 'fw-bold',
    },
  },
  {
    code: 'valueLabel',
    type: 'text' as InputType,
    required: true,
    label: `${QUESTION.CAPITALIZED_SINGULAR} in alternative language`,
    placeholder: `Type ${QUESTION.SINGULAR}`,
    classes: {
      label: 'fw-bold',
    },
  },
  {
    code: 'instructions',
    type: 'textarea' as InputType,
    label: 'Instructions in alternative language',
    placeholder: 'Type in the translation of any instructions',
    classes: {
      label: 'fw-bold',
    },
  },
]);

interface LanguageFormContainerProps {
  formData: Form;
  onChange: (e: React.ChangeEvent<{ name: string, value: any }>) => void;
}

const LanguageFormContainer = (props: LanguageFormContainerProps) => {
  const { formData, onChange } = props;
  const [showLanguageForm, setShowLanguageForm] = useState(false);
  const alternativeKeys = Object.keys(formData.alternatives ?? {}) ?? [];
  const existingLanguages = alternativeKeys.filter(k => availableLanguages.includes(k));
  const [newLanguageFormData, setNewLanguageForm] = useState<{ languageCode?: string, valueLabel?: string, instructions?: string }>({});

  const removeLanguage = (language: string) => {
    const alternatives = formData.alternatives ?? {};
    if (alternatives && alternatives[language].alternativeType === 'language') {
      delete alternatives[language];
      replaceAlternatives(alternatives);
    }
  }

  const updateFormByLanguage = (language: string) => {
    return (e: React.ChangeEvent<{ name: string, value: string | number | undefined }>) => {
      const name = e.target.name as AlternativeFieldCodes;
      const value = e.target.value;
      const alternatives = { ...formData.alternatives } ?? {};

      if (!alternatives[language]) {
        alternatives[language] = {
          name: formData.name,
          alternativeType: 'language',
          valueLabel: formData.valueLabel,
        }
      }

      if (name === 'languageCode' && value && value !== language) {
        alternatives[value] = alternatives[language];
        alternatives[value].languageCode = String(value);
        delete alternatives[language];
      } else {
        alternatives[language][name] = value !== undefined ? String(value) : '';
      }
      replaceAlternatives(alternatives);
    }
  };

  const updateNewLanguageForm = (e: React.ChangeEvent<{ name: string, value: string | number | undefined }>) => {
    const name = e.target.name;
    const value = e.target.value;
    const alternative = {
      ...newLanguageFormData,
      [name]: value
    };

    // Field can now live inside the main alternatives
    if (alternative.languageCode) {
      const language = alternative.languageCode;
      const alternatives = { ...formData.alternatives } ?? {};

      if (!alternatives[language]) {
        alternatives[language] = {
          name: formData.name,
          alternativeType: 'language',
          valueLabel: formData.valueLabel,
        }
      }
      alternatives[language].languageCode = alternative.languageCode ?? '';
      alternatives[language].valueLabel = alternative.valueLabel ?? '';
      alternatives[language].instructions = alternative.valueLabel ?? '';
      replaceAlternatives(alternatives);
      setNewLanguageForm({});
      setShowLanguageForm(false);
      return;
    }

    // Or else just store in local state until we can determine the language
    setNewLanguageForm({
      ...newLanguageFormData,
      [name]: value
    });
  };

  const replaceAlternatives = (alternatives: { [key: string]: Alternative | LanguageAlternative }) => {
    onChange({
      target: {
        name: 'alternatives',
        value: alternatives
      }
    } as unknown as React.ChangeEvent<{ name: string, value: string | number | undefined }>);
  }

  const getFilteredLanguageFields = (excludeLanguages: string[]) => {
    const fields = getLanguageFields();
    fields[0].options = fields[0].options?.filter((o: FormOptionInterface) => o.value && !excludeLanguages.includes(String(o.value))) ?? [];
    return fields;
  };

  const newFormFields = getFilteredLanguageFields(existingLanguages);
  const cannotAddNewLanguages = (newFormFields[0].options?.length === 0);

  return (
    <div className='mt-4'>
      {existingLanguages.map(k => {
        if (formData.alternatives) {
          return <Fragment key={`language_${k}`}>
            <FormGenerator uniqueKey={`lang-${k}`} fields={getFilteredLanguageFields(availableLanguages.filter(l => l !== k))} form={formData.alternatives[k]} updateForm={updateFormByLanguage(k)} />
            <div className='text-right mb-3'>
              <Button size='sm' color='link' onClick={() => removeLanguage(k)}>
                <i className='fa fa-trash mr-2' />Remove alternative language
              </Button>
            </div>
          </Fragment>;
        }
        return <></>;
      })}
      {cannotAddNewLanguages ?
        <></>
        :
        showLanguageForm
          ?
          <>
            <FormGenerator uniqueKey='new-lang' fields={newFormFields} form={newLanguageFormData} updateForm={updateNewLanguageForm} />
            <div className='text-right'>
              <Button size='sm' color='link' onClick={() => setShowLanguageForm(false)}>
                Cancel
              </Button>
            </div>
          </>
          :
          <div className='text-right'>
            <Button size='sm' color='link' onClick={() => setShowLanguageForm(true)}>
              <i className='fa fa-plus mr-2' />Add alternative language
            </Button>
          </div>
      }
    </div>
  );
}

export default LanguageFormContainer;
